import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, debounceTime, map, startWith, tap } from 'rxjs';

/**
 * A reusable, generic multi-select wrapper for handling asynchronous data.
 *
 * Similar to AsyncSelectComponent but supports selecting multiple options.
 * Selected items are displayed as a comma-separated list in the trigger.
 * The value emitted and received is always T[].
 *
 * IMPORTANT: Always provide a `compareWith` function when T is an object so that
 * mat-select can correctly match option values against the form control value
 * (e.g. compare by id). Without it Angular falls back to reference equality,
 * which breaks checkbox state when options are loaded asynchronously.
 */
@Component({
  selector: 'rms-async-multi-select',
  imports: [
    FormsModule,
    MatFormField,
    MatHint,
    MatOption,
    MatProgressSpinner,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    MatSelectTrigger,
    TranslateModule,
    NgTemplateOutlet,
    NgClass,
    MatInput,
    AsyncPipe,
  ],
  templateUrl: './async-multi-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AsyncMultiSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsyncMultiSelectComponent<T> implements OnInit, ControlValueAccessor {
  @Input({ required: true }) public set options(options: T[] | null) {
    this.source$.next(options ?? []);
  }

  @Input() public set error(error: string | null) {
    this.setDisabledState(!!error);

    this.error$.next(error);
  }

  @Input() public set loading(loading: boolean) {
    this.setDisabledState(loading);

    this.loading$.next(loading);
  }

  @Input() public placeholder = '';

  @Input() public searchable = false;

  @Input() public displayWith: ((value: T) => string) | null = null;
  @Input() public searchFn: ((search: string, options: T[]) => T[]) | null = null;
  @Input() public compareWith: (a: T, b: T) => boolean = (a: unknown, b: unknown) => a === b;

  @Output() public retry = new EventEmitter<void>();

  @ContentChild('optionTemplate')
  protected optionTemplateRef?: TemplateRef<{ $implicit: T }>;

  private readonly destroy$ = inject(DestroyRef);

  protected searchControl = new FormControl<string | null>(null);
  protected control = new FormControl<T[]>([]);
  protected source$ = new BehaviorSubject<T[]>([]);
  protected loading$ = new BehaviorSubject<boolean>(false);
  protected error$ = new BehaviorSubject<string | null>(null);
  protected filteredOptions$ = combineLatest([
    this.source$,
    this.searchControl.valueChanges.pipe(startWith(null), debounceTime(300)),
  ]).pipe(
    map(([source, search]) => (this.searchFn && search && search.length > 0 ? this.searchFn(search, source) : source))
  );

  private onChange?: (value: T[]) => void;
  private onTouched?: () => void;

  protected get selectedLabel(): string {
    const values = this.control.value ?? [];

    if (!this.displayWith) {
      return values.join(', ');
    }

    return values.map((v) => this.displayWith!(v)).join(', ');
  }

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(
        tap((value) => {
          this.onTouched?.();
          this.onChange?.(value ?? []);
        }),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe();
  }

  writeValue(value: T[] | null): void {
    this.control.patchValue(value ?? [], { emitEvent: false });
  }

  public registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled
      ? this.control.enabled && this.control.disable({ emitEvent: false })
      : this.control.disabled && this.control.enable({ emitEvent: false });
  }

  public isInOptions(selected: T, options: T[]): boolean {
    return options.some((o) => this.compareWith(o, selected));
  }
}
